import React from "react";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import styled from "styled-components";
import { IndexerCluster } from "@graylog/server-api";
import { Spinner } from "components/common";
import { Row, Col } from "components/bootstrap";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import { IndexerClusterHealthSummary } from "components/indexers";
import usePermissions from "hooks/usePermissions";
import IndexerClusterHealthError from "./IndexerClusterHealthError";
const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;
const GET_INDEXER_CLUSTER_HEALTH = "indexerCluster.health";
const GET_INDEXER_CLUSTER_NAME = "indexerCluster.name";
const getIndexerClusterHealth = () => IndexerCluster.clusterHealth({ requestShouldExtendSession: false });
const getIndexerClusterName = () => IndexerCluster.clusterInfo({ requestShouldExtendSession: false });
const useLoadHealthAndName = (enabled) => {
  const options = { refetchInterval: 5e3, retry: 0, enabled };
  const [
    {
      data: healthData,
      isFetching: healthIsFetching,
      error: healthError,
      isSuccess: healthIsSuccess,
      isRefetching: healthIsRefetching
    },
    {
      data: nameData,
      isFetching: nameIsFetching,
      error: nameError,
      isSuccess: nameIsSuccess,
      isRefetching: nameIsRefetching
    }
  ] = useQueries({
    queries: [
      { queryKey: [GET_INDEXER_CLUSTER_HEALTH], queryFn: getIndexerClusterHealth, ...options },
      { queryKey: [GET_INDEXER_CLUSTER_NAME], queryFn: getIndexerClusterName, ...options }
    ]
  });
  return {
    health: healthData,
    name: nameData,
    error: healthError || nameError,
    loading: (healthIsFetching || nameIsFetching) && !healthIsRefetching && !nameIsRefetching,
    isSuccess: healthIsSuccess && nameIsSuccess
  };
};
const IndexerClusterHealth = ({ minimal = false }) => {
  const { t } = useTranslation("indexsets");
  const { isPermitted } = usePermissions();
  const userHasRequiredPermissions = isPermitted("indexercluster:read");
  const { health, name, loading, error, isSuccess } = useLoadHealthAndName(userHasRequiredPermissions);
  if (!userHasRequiredPermissions) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, !minimal && /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("h2", null, t("indexers.cluster_health.title", { distribution: (name == null ? void 0 : name.distribution) || "Elasticsearch" })), (name == null ? void 0 : name.distribution) === "OpenSearch" ? /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.OPEN_SEARCH_SETUP,
      text: t("indexers.cluster_health.docs_open_search"),
      displayIcon: true
    }
  ) : /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.CONFIGURING_ES,
      text: t("indexers.cluster_health.docs_elasticsearch"),
      displayIcon: true
    }
  )), isSuccess && /* @__PURE__ */ React.createElement(IndexerClusterHealthSummary, { health, name }), loading && /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Spinner, null)), error && /* @__PURE__ */ React.createElement(IndexerClusterHealthError, { error, name })));
};
export default IndexerClusterHealth;
